import { BLOCK_ID_KEY, isBlockId, NODE_STATE_KEY } from "./block-id";
import { CORE_REGISTRY, type CapabilityRegistry } from "./capabilities";
import { DEFAULT_LIMITS, tableLimits, type DocumentLimits } from "./limits";
import { measureTable, TABLE_NODE_TYPE } from "./tables";
import { CORE_TOKEN_REGISTRY, type TokenRegistry } from "./tokens";
import {
  CURRENT_SCHEMA_VERSION,
  DOCUMENT_FORMAT,
  type EditorDocument,
  type SerializedNodeLike,
} from "./types";

export type DocumentIssueCode =
  | "invalid-json"
  | "not-a-document"
  | "future-version"
  | "migration-failed"
  | "invalid-envelope"
  | "limit-exceeded"
  | "unknown-node"
  | "malformed-node";

export interface DocumentIssue {
  code: DocumentIssueCode;
  message: string;
  /** Child indexes from the root to the offending node, when the issue is about one node. */
  path?: readonly number[];
  /** Serialized node types the loaded capabilities do not cover. */
  nodeTypes?: readonly string[];
  /** Which limit a `limit-exceeded` issue is about. */
  limit?: LimitName;
}

/** `table` is a table with more rows or columns than `maxTableRows` and `maxTableColumns` allow. */
export type LimitName = "bytes" | "nodes" | "depth" | "table";

export type ValidationResult =
  | { ok: true; document: EditorDocument }
  | { ok: false; issue: DocumentIssue };

export interface ValidateOptions {
  registry?: CapabilityRegistry;
  limits?: DocumentLimits;
  /** The product's token types. Tokens of other types are kept and shown by their label. */
  tokens?: TokenRegistry;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const fail = (issue: DocumentIssue): ValidationResult => ({ ok: false, issue });

export interface NodeWalkOptions {
  registry: CapabilityRegistry;
  limits: DocumentLimits;
  tokens: TokenRegistry;
  /**
   * Set for content that is not a whole document (a clipboard payload, an import): its top-level
   * nodes sit where a root's children would, and a `root` among them is malformed.
   */
  fragment?: boolean;
}

export interface NodeWalk {
  issue: DocumentIssue | null;
  /** Nodes visited and the deepest level reached, meaningful when there is no issue. */
  nodes: number;
  depth: number;
}

/**
 * The one walk every piece of untrusted content goes through, stored or pasted: node shape,
 * guards, block IDs, node count, and depth. Iterative, so hostile nesting trips the depth limit
 * instead of overflowing the stack.
 */
export function walkNodes(roots: readonly unknown[], options: NodeWalkOptions): NodeWalk {
  const { registry, limits } = options;
  const fragment = options.fragment === true;
  const maxTable = tableLimits(limits);
  const unknownTypes = new Set<string>();
  const blockIds = new Set<string>();
  let firstUnknownPath: readonly number[] | undefined;
  let count = 0;
  let depth = 0;
  const stop = (issue: DocumentIssue): NodeWalk => ({ issue, nodes: count, depth });
  const stack: { node: unknown; path: readonly number[]; parent: string | null }[] = [];
  for (let index = roots.length - 1; index >= 0; index--) {
    stack.push({ node: roots[index], path: fragment ? [index] : [], parent: null });
  }
  while (stack.length > 0) {
    const { node, path, parent } = stack.pop()!;
    if (++count > limits.maxNodes) {
      return stop({
        code: "limit-exceeded",
        message: `More than ${limits.maxNodes} nodes.`,
        limit: "nodes",
      });
    }
    if (path.length > limits.maxDepth) {
      return stop({
        code: "limit-exceeded",
        message: `Nested deeper than ${limits.maxDepth}.`,
        limit: "depth",
        path,
      });
    }
    depth = Math.max(depth, path.length);
    if (!isRecord(node) || typeof node.type !== "string" || typeof node.version !== "number") {
      return stop({ code: "malformed-node", message: "Node has no type or version.", path });
    }
    if (fragment && node.type === "root") {
      return stop({ code: "malformed-node", message: "A root inside content.", path });
    }
    const state = node[NODE_STATE_KEY];
    if (state !== undefined) {
      if (!isRecord(state)) {
        return stop({
          code: "malformed-node",
          message: `${node.type}: state is not an object.`,
          path,
        });
      }
      const blockId = state[BLOCK_ID_KEY];
      if (blockId !== undefined) {
        if (!isBlockId(blockId)) {
          return stop({
            code: "malformed-node",
            message: `${node.type}: block ID is not valid.`,
            path,
          });
        }
        // Two blocks answering to one ID would make every block-addressed edit ambiguous.
        if (blockIds.has(blockId)) {
          return stop({
            code: "malformed-node",
            message: `Duplicate block ID "${blockId}".`,
            path,
          });
        }
        blockIds.add(blockId);
      }
    }
    const guard = registry.guardFor(node.type);
    if (guard === undefined) {
      unknownTypes.add(node.type);
      firstUnknownPath ??= path;
    } else {
      const problem = guard(node as SerializedNodeLike, {
        tokens: options.tokens,
        parent,
        fragment,
      });
      if (problem !== null) {
        return stop({ code: "malformed-node", message: `${node.type}: ${problem}.`, path });
      }
    }
    // Checked for every type, known or not: an unknown node is still rendered from its children.
    if (node.children !== undefined && !Array.isArray(node.children)) {
      return stop({
        code: "malformed-node",
        message: `${node.type}: children is not a list.`,
        path,
      });
    }
    // Measured before its cells are visited, so a table that is too large is reported as that and
    // not as however many nodes it happens to hold.
    if (
      node.type === TABLE_NODE_TYPE &&
      measureTable(node, maxTable.rows, maxTable.columns) === null
    ) {
      return stop({
        code: "limit-exceeded",
        message: `A table larger than ${maxTable.rows} rows by ${maxTable.columns} columns.`,
        limit: "table",
        path,
      });
    }
    if (Array.isArray(node.children)) {
      for (let index = node.children.length - 1; index >= 0; index--) {
        stack.push({ node: node.children[index], path: [...path, index], parent: node.type });
      }
    }
  }

  if (unknownTypes.size > 0) {
    const nodeTypes = [...unknownTypes].toSorted();
    return stop({
      code: "unknown-node",
      message: `Unsupported content: ${nodeTypes.join(", ")}.`,
      nodeTypes,
      ...(firstUnknownPath === undefined ? {} : { path: firstUnknownPath }),
    });
  }
  return { issue: null, nodes: count, depth };
}

/**
 * Checks a current-version document before it reaches Lexical. Anything Lexical would drop,
 * normalize away, or throw on is reported here instead, so the caller can show a recovery state
 * rather than an editor holding less than the stored document.
 */
export function validateDocument(input: unknown, options: ValidateOptions = {}): ValidationResult {
  const registry = options.registry ?? CORE_REGISTRY;
  const limits = options.limits ?? DEFAULT_LIMITS;
  const tokens = options.tokens ?? CORE_TOKEN_REGISTRY;

  if (!isRecord(input) || input.format !== DOCUMENT_FORMAT) {
    return fail({ code: "not-a-document", message: `Not a ${DOCUMENT_FORMAT} document.` });
  }
  if (input.schemaVersion !== CURRENT_SCHEMA_VERSION) {
    return fail({
      code: "invalid-envelope",
      message: `Expected schema version ${CURRENT_SCHEMA_VERSION}.`,
    });
  }
  if (typeof input.documentId !== "string" || input.documentId === "") {
    return fail({ code: "invalid-envelope", message: "documentId is missing." });
  }
  const engine = input.engine;
  if (!isRecord(engine) || engine.name !== "lexical" || typeof engine.version !== "string") {
    return fail({ code: "invalid-envelope", message: "engine is missing or not lexical." });
  }
  const content = input.content;
  if (!isRecord(content) || !isRecord(content.root) || content.root.type !== "root") {
    return fail({ code: "invalid-envelope", message: "content.root is missing." });
  }

  const issue = walkNodes([content.root], { registry, limits, tokens }).issue;
  if (issue !== null) return fail(issue);
  return { ok: true, document: input as unknown as EditorDocument };
}
